import { useEffect, useRef, useState } from "react";
import { fetchPrayerTimes, fetchScreenData } from "../../services/api";
import { PrayerTimes } from "../../types/PrayerTypes";
import { toMinutes } from "../../utils/nextPrayerStep";
import { HomeSettings, audioChoiceFor, azanSettingsFor } from "./homeSettings";
import { checkStatusUrl } from "./liveAudio";
import { azanAlertsForDay, nowIn } from "./useAzanAlerts";

const REFRESH_MS = 60 * 60 * 1000;
const CHECK_MS = 15_000;
// The clock's grace: the screen stays on this long after the time it came on for.
const GRACE_MIN = 5;

const BEGINS = [
  "SehriEnd", "BegFajr", "Sunrise", "BegDhuhr", "BegAsar", "BegMaghrib", "BegEsha",
] as const;
const JAMAAH = ["JamFajr", "JamDhuhr", "JamAsar", "JamMaghrib", "JamEsha"] as const;

interface LiveCheck {
  url: string;
  // live.json: live when one of these is in its `live` list. Otherwise the backend's isLive.
  slugs?: string[];
}

export interface WakeSchedule {
  // When the app switches a switched-off screen on (it wakes up to 90 s early): each azan, and the
  // start of each salah window. App 1.2 reads it under this name.
  azanAt: number[];
  checks: LiveCheck[];
  // App 1.3+: Firebase topics the stream box pushes to the moment the home masjid goes live
  // (live-<slug> for our streams, live-m<masjid id> for eMasjid-family and Mixlr masjids).
  topics?: string[];
}

// App 1.2+ (MainActivity) adds this. Once the screen is switched off the page stops running, so it
// tells the app when to switch the screen back on (azan times, the home masjid going live) and
// whether something is playing, so the app can let the screen go off again afterwards.
const screenChannel = () =>
  (window as Window & { m247Screen?: { postMessage: (message: string) => void } }).m247Screen;

export const canWakeScreen = (): boolean => !!screenChannel();

// Set by app 1.3+, which subscribes to the topics it's given.
const canTakePushes = (): boolean =>
  !!(window as Window & { m247Push?: boolean }).m247Push;

// The household's salah windows for a day, as [start, end) minutes since midnight, merged.
export const salahWindowsForDay = (
  day: PrayerTimes,
  wake: { begins: number; jamaah: number },
  friday: boolean,
): [number, number][] => {
  const windows: [number, number][] = [];
  const add = (time: string | undefined, before: number) => {
    const at = toMinutes(time);
    if (at !== null && before > 0) windows.push([at - before, at + GRACE_MIN]);
  };
  for (const key of BEGINS) add(day[key], wake.begins);
  // Some masjids publish Maghrib's jama'ah only; it starts then, as the azan does.
  if (toMinutes(day.BegMaghrib) === null) add(day.JamMaghrib, wake.begins);
  const jumuah = [day.Jummah1, day.Jummah2, day.Jummah3, day.Jummah4].filter(
    (t) => toMinutes(t) !== null,
  );
  for (const key of JAMAAH) {
    if (friday && key === "JamDhuhr" && jumuah.length) jumuah.forEach((t) => add(t, wake.jamaah));
    else add(day[key], wake.jamaah);
  }
  windows.sort((a, b) => a[0] - b[0]);
  return windows.reduce<[number, number][]>((merged, w) => {
    const last = merged[merged.length - 1];
    if (last && w[0] <= last[1]) last[1] = Math.max(last[1], w[1]);
    else merged.push([w[0], w[1]]);
    return merged;
  }, []);
};

const nextDay = (date: string) =>
  new Date(Date.parse(`${date}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

const buildSchedule = async (
  settings: HomeSettings,
): Promise<{ schedule: WakeSchedule; windows: [number, number][] }> => {
  const home = settings.favourites[0];
  const screen = await fetchScreenData(home.id, "home");
  const now = nowIn(screen.timeZone || "Europe/London");
  const minuteStart = Math.floor(Date.now() / 60_000) * 60_000;
  const toMs = (offset: number, minute: number) =>
    minuteStart + (offset + minute - now.minutes) * 60_000;

  const azan = azanSettingsFor(settings);
  const wake = { begins: settings.wake?.begins ?? 0, jamaah: settings.wake?.jamaah ?? 0 };
  const azanAt: number[] = [];
  const windows: [number, number][] = [];
  if (azan.atStart || azan.beforeJamaah || wake.begins || wake.jamaah) {
    const tomorrow = nextDay(now.date);
    const days = [
      { date: now.date, offset: 0, friday: now.friday },
      { date: tomorrow, offset: 1440, friday: new Date(`${tomorrow}T12:00:00Z`).getUTCDay() === 5 },
    ];
    for (const day of days) {
      const times = await fetchPrayerTimes(day.date, home.id).catch(() => null);
      if (!times) continue;
      if (azan.atStart || azan.beforeJamaah) {
        for (const alert of azanAlertsForDay(times, azan, day.friday)) {
          azanAt.push(toMs(day.offset, alert.minute));
        }
      }
      for (const [start, end] of salahWindowsForDay(times, wake, day.friday)) {
        windows.push([toMs(day.offset, start), toMs(day.offset, end)]);
        azanAt.push(toMs(day.offset, start));
      }
    }
  }

  // Only a broadcast that plays by itself is worth switching the screen on for.
  const checks: LiveCheck[] = [];
  const topics: string[] = [];
  const audio = screen.homeAudio;
  if (audio && audioChoiceFor(settings, home) === "auto") {
    if (audio.liveStreams.length) {
      checks.push({ url: audio.liveStatusUrl, slugs: audio.liveStreams.map((s) => s.slug) });
      topics.push(...audio.liveStreams.map((s) => `live-${s.slug}`));
    } else if (audio.partnerAudio) {
      checks.push({ url: checkStatusUrl(audio.partnerAudio) });
      // MyMasjid has no listing, so nothing pushes for it; the app's minute check stays.
      if (audio.partnerAudio.provider !== "mymasjid") topics.push(`live-m${home.id}`);
    }
  }
  return {
    schedule: {
      azanAt: azanAt.filter((at) => at > Date.now()).sort((a, b) => a - b),
      checks,
      ...(canTakePushes() ? { topics } : {}),
    },
    windows: windows.filter(([, end]) => end > Date.now()),
  };
};

// `settings` is null without a code: nothing wakes the screen then.
export const useWakeSchedule = (settings: HomeSettings | null, busy: boolean) => {
  const sent = useRef("");
  const windows = useRef<[number, number][]>([]);
  // Inside a salah window the screen stays on, as if something were playing.
  const [inWindow, setInWindow] = useState(false);

  useEffect(() => {
    const channel = screenChannel();
    if (!channel) return;
    let cancelled = false;
    const send = async () => {
      const built = settings?.favourites[0]
        ? await buildSchedule(settings).catch(() => null)
        : { schedule: { azanAt: [], checks: [] }, windows: [] };
      // Offline with nothing cached: the app keeps the last schedule it was given.
      if (!built || cancelled) return;
      windows.current = built.windows;
      const message = JSON.stringify({ schedule: built.schedule });
      if (message === sent.current) return;
      sent.current = message;
      channel.postMessage(message);
    };
    send();
    const timer = window.setInterval(send, REFRESH_MS);
    const check = window.setInterval(() => {
      const now = Date.now();
      setInWindow(windows.current.some(([start, end]) => now >= start && now < end));
    }, CHECK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.clearInterval(check);
    };
  }, [settings]);

  const keepOn = busy || inWindow;
  useEffect(() => {
    screenChannel()?.postMessage(JSON.stringify({ busy: keepOn }));
  }, [keepOn]);
};
